/**
 * Search Index Manager
 *
 * Manages the lunr.js search index with field boosting,
 * incremental updates, and IndexedDB persistence.
 */

import lunr from 'lunr';
import { v4 as uuidv4 } from 'uuid';
import type {
  SearchDocument,
  SearchQuery,
  SearchResult,
  SearchResults,
  SearchIndexData,
  SearchSettings,
  SearchHistoryItem,
} from './types';
import { DEFAULT_SEARCH_SETTINGS } from './types';
import { IndexedDBSearchStorage } from './storage/IndexedDBSearchStorage';

export class SearchIndexManager {
  private index: lunr.Index | null = null;
  private documents: Map<string, SearchDocument> = new Map();
  private storage: IndexedDBSearchStorage;
  private settings: SearchSettings;
  private indexVersion = 1;
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private updateQueue: Set<string> = new Set();
  private updateTimer: NodeJS.Timeout | null = null;
  private worker: Worker | null = null;
  private searchCache: Map<string, SearchResults> = new Map();
  private cacheTimer: NodeJS.Timeout | null = null;

  constructor(settings?: Partial<SearchSettings>) {
    this.settings = { ...DEFAULT_SEARCH_SETTINGS, ...settings };
    this.storage = new IndexedDBSearchStorage();
  }

  /**
   * Initialize the search index
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._initialize();
    await this.initPromise;
    this.isInitialized = true;
  }

  private async _initialize(): Promise<void> {
    // Load settings from storage
    const storedSettings = await this.storage.getSettings();
    this.settings = { ...this.settings, ...storedSettings };

    // Try to load existing index
    const indexData = await this.storage.loadIndex();

    if (indexData) {
      try {
        // Restore index from storage
        this.index = lunr.Index.load(JSON.parse(indexData.indexData));
        this.indexVersion = indexData.version;

        // Load documents
        const documents = await this.storage.getAllDocuments();
        documents.forEach((doc) => {
          this.documents.set(doc.id, doc);
        });
      } catch (error) {
        console.error('Failed to load search index, rebuilding:', error);
        await this.rebuildIndex();
      }
    } else {
      // Build new index
      await this.rebuildIndex();
    }

    // Start cache cleanup timer
    this.startCacheCleanup();

    // Initialize Web Worker if available
    if (typeof Worker !== 'undefined' && this.settings.autoIndex) {
      try {
        // Worker will be initialized when needed
      } catch (error) {
        console.warn('Web Worker not available for search indexing:', error);
      }
    }
  }

  /**
   * Build a new search index from scratch
   */
  private async rebuildIndex(): Promise<void> {
    // Load all documents from storage
    const documents = await this.storage.getAllDocuments();

    // Build lunr index
    this.index = lunr(function () {
      // Configure fields with boosts
      this.field('title', { boost: DEFAULT_SEARCH_SETTINGS.fieldBoosts.title });
      this.field('content', {
        boost: DEFAULT_SEARCH_SETTINGS.fieldBoosts.content,
      });
      this.field('tags', { boost: DEFAULT_SEARCH_SETTINGS.fieldBoosts.tags });
      this.field('type', { boost: DEFAULT_SEARCH_SETTINGS.fieldBoosts.type });
      this.field('metadata', {
        boost: DEFAULT_SEARCH_SETTINGS.fieldBoosts.metadata,
      });

      // Configure pipeline
      this.pipeline.remove(lunr.stemmer);
      this.pipeline.add(lunr.stemmer);

      // Add documents
      documents.forEach((doc) => {
        const indexDoc = {
          id: doc.id,
          title: doc.title,
          content: doc.content,
          tags: doc.tags.join(' '),
          type: doc.type,
          metadata: JSON.stringify(doc.metadata || {}),
        };
        this.add(indexDoc);
      });
    });

    // Store documents in memory
    this.documents.clear();
    documents.forEach((doc) => {
      this.documents.set(doc.id, doc);
    });

    // Save index to storage
    await this.saveIndex();
  }

  /**
   * Save the current index to storage
   */
  private async saveIndex(): Promise<void> {
    if (!this.index) return;

    const indexData: SearchIndexData = {
      id: 'main',
      indexData: JSON.stringify(this.index.toJSON()),
      version: this.indexVersion,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      documentCount: this.documents.size,
      sizeBytes: 0, // Will be calculated by storage
      isCompressed: this.settings.enableCompression,
    };

    await this.storage.saveIndex(indexData);
  }

  /**
   * Add or update a document in the index
   */
  async indexDocument(document: SearchDocument): Promise<void> {
    await this.initialize();

    // Update document timestamp
    document.lastIndexed = new Date().toISOString();

    // Store document
    this.documents.set(document.id, document);
    await this.storage.saveDocument(document);

    // Queue for index update
    if (this.settings.autoIndex) {
      this.queueIndexUpdate(document.id);
    } else {
      // Update immediately
      await this.updateIndexForDocument(document);
    }
  }

  /**
   * Queue a document for index update
   */
  private queueIndexUpdate(documentId: string): void {
    this.updateQueue.add(documentId);

    // Clear existing timer
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }

    // Set new timer with debounce
    this.updateTimer = setTimeout(async () => {
      await this.processUpdateQueue();
    }, this.settings.indexDebounceMs);
  }

  /**
   * Process queued index updates
   */
  private async processUpdateQueue(): Promise<void> {
    if (this.updateQueue.size === 0) return;

    // Clear the queue
    this.updateQueue.clear();

    // Rebuild index with updated documents
    await this.rebuildIndex();

    // Clear cache after index update
    this.clearCache();
  }

  /**
   * Update index for a single document
   */
  private async updateIndexForDocument(
    _document: SearchDocument
  ): Promise<void> {
    if (!this.index) return;

    // Rebuild index (lunr doesn't support in-place updates)
    // For large indexes, consider using a more sophisticated approach
    await this.rebuildIndex();
  }

  /**
   * Remove a document from the index
   */
  async removeDocument(documentId: string): Promise<void> {
    await this.initialize();

    // Remove from memory
    this.documents.delete(documentId);

    // Remove from storage
    await this.storage.removeDocument(documentId);

    // Rebuild index
    await this.rebuildIndex();

    // Clear cache
    this.clearCache();
  }

  /**
   * Search the index
   */
  async search(query: SearchQuery): Promise<SearchResults> {
    await this.initialize();

    const startTime = Date.now();

    // Check cache
    const cacheKey = this.getCacheKey(query);
    const cached = this.searchCache.get(cacheKey);
    if (cached) {
      return { ...cached, fromCache: true };
    }

    if (!this.index) {
      return {
        query,
        results: [],
        totalCount: 0,
        executionTime: Date.now() - startTime,
        fromCache: false,
      };
    }

    // Build lunr query
    let lunrResults: lunr.Index.Result[] = [];

    try {
      // Parse query string before the search
      const queryTerms = this.parseQueryString(query.query);

      // Use the search with a query builder
      lunrResults = (this.index as any).search(function (builder: any) {
        queryTerms.forEach((term: string) => {
          // Configure term options
          const termConfig: any = {};

          if (query.fuzzy) {
            termConfig.editDistance = 2;
          }

          if (query.wildcard) {
            termConfig.wildcard = lunr.Query.wildcard.TRAILING;
          }

          // Add field-specific searches
          if (query.fields && query.fields.length > 0) {
            query.fields.forEach((field) => {
              builder.term(term, {
                ...termConfig,
                fields: [field],
              });
            });
          } else {
            // Search all fields
            builder.term(term, termConfig);
          }
        });
      });
    } catch (error) {
      console.error('Search error:', error);
      // Fall back to simple string search
      lunrResults = this.fallbackSearch(query.query);
    }

    // Apply filters
    let filteredResults = this.applyFilters(lunrResults, query);

    // Get total count before pagination
    const totalCount = filteredResults.length;

    // Apply pagination
    const offset = query.offset || 0;
    const limit = query.limit || 20;
    filteredResults = filteredResults.slice(offset, offset + limit);

    // Build results with documents and highlights
    const results: SearchResult[] = filteredResults.map((result) => {
      const document = this.documents.get(result.ref);
      if (!document) {
        throw new Error(`Document not found: ${result.ref}`);
      }

      return {
        ref: result.ref,
        score: result.score,
        document,
        matchData: result.matchData,
        highlights: this.generateHighlights(document, result.matchData),
      };
    });

    const searchResults: SearchResults = {
      query,
      results,
      totalCount,
      executionTime: Date.now() - startTime,
      fromCache: false,
    };

    // Cache results
    this.searchCache.set(cacheKey, searchResults);

    // Add to history
    const historyItem: SearchHistoryItem = {
      id: uuidv4(),
      query,
      resultCount: totalCount,
      timestamp: new Date().toISOString(),
      successful: true,
    };
    await this.storage.addToHistory(historyItem);

    return searchResults;
  }

  /**
   * Parse query string into terms
   */
  private parseQueryString(queryString: string): string[] {
    // Handle quoted phrases
    const phrases = queryString.match(/"([^"]+)"/g) || [];
    let remaining = queryString;

    phrases.forEach((phrase) => {
      remaining = remaining.replace(phrase, '');
    });

    // Extract individual terms
    const terms = remaining
      .split(/\s+/)
      .filter((term) => term.length > 0)
      .concat(phrases.map((p) => p.replace(/"/g, '')));

    return terms;
  }

  /**
   * Fallback search when lunr fails
   */
  private fallbackSearch(query: string): lunr.Index.Result[] {
    const results: lunr.Index.Result[] = [];
    const queryLower = query.toLowerCase();

    this.documents.forEach((doc, id) => {
      const searchText =
        `${doc.title} ${doc.content} ${doc.tags.join(' ')}`.toLowerCase();
      if (searchText.includes(queryLower)) {
        results.push({
          ref: id,
          score: 1,
          matchData: { metadata: {}, combine: () => {} } as any,
        });
      }
    });

    return results;
  }

  /**
   * Apply filters to search results
   */
  private applyFilters(
    results: lunr.Index.Result[],
    query: SearchQuery
  ): lunr.Index.Result[] {
    return results.filter((result) => {
      const document = this.documents.get(result.ref);
      if (!document) return false;

      // Type filter
      if (query.type && query.type !== 'all' && document.type !== query.type) {
        return false;
      }

      // Tag filter
      if (query.tags && query.tags.length > 0) {
        const hasMatchingTag = query.tags.some((tag) =>
          document.tags.includes(tag)
        );
        if (!hasMatchingTag) return false;
      }

      // Date range filter
      if (query.dateRange) {
        const docDate = new Date(document.lastModified);
        const fromDate = new Date(query.dateRange.from);
        const toDate = new Date(query.dateRange.to);
        if (docDate < fromDate || docDate > toDate) {
          return false;
        }
      }

      // Metadata filter
      if (query.metadata) {
        const docMetadata = document.metadata || {};
        for (const [key, value] of Object.entries(query.metadata)) {
          if (docMetadata[key] !== value) {
            return false;
          }
        }
      }

      return true;
    });
  }

  /**
   * Generate highlighted snippets
   */
  private generateHighlights(
    document: SearchDocument,
    matchData: any
  ): SearchResult['highlights'] {
    const highlights: SearchResult['highlights'] = {};

    // Simple highlight generation
    // In production, use a more sophisticated approach
    const terms = Object.keys(matchData.metadata);

    if (terms.length > 0) {
      const regex = new RegExp(`(${terms.join('|')})`, 'gi');

      // Highlight title
      if (document.title) {
        highlights.title = document.title.replace(regex, '<mark>$1</mark>');
      }

      // Highlight content snippet
      if (document.content) {
        const snippet = this.extractSnippet(document.content, terms[0]);
        highlights.content = snippet.replace(regex, '<mark>$1</mark>');
      }

      // Highlight matching tags
      highlights.tags = document.tags.filter((tag) =>
        terms.some((term) => tag.toLowerCase().includes(term.toLowerCase()))
      );
    }

    return highlights;
  }

  /**
   * Extract a snippet around a search term
   */
  private extractSnippet(content: string, term: string): string {
    const index = content.toLowerCase().indexOf(term.toLowerCase());
    if (index === -1) {
      return content.substring(0, 150) + '...';
    }

    const start = Math.max(0, index - 50);
    const end = Math.min(content.length, index + term.length + 100);

    let snippet = content.substring(start, end);
    if (start > 0) snippet = '...' + snippet;
    if (end < content.length) snippet = snippet + '...';

    return snippet;
  }

  /**
   * Get cache key for a query
   */
  private getCacheKey(query: SearchQuery): string {
    return JSON.stringify({
      q: query.query,
      f: query.fields,
      t: query.type,
      tg: query.tags,
      d: query.dateRange,
      m: query.metadata,
      fz: query.fuzzy,
      w: query.wildcard,
      l: query.limit,
      o: query.offset,
    });
  }

  /**
   * Clear search cache
   */
  private clearCache(): void {
    this.searchCache.clear();
  }

  /**
   * Start cache cleanup timer
   */
  private startCacheCleanup(): void {
    if (this.cacheTimer) {
      clearInterval(this.cacheTimer);
    }

    this.cacheTimer = setInterval(
      () => {
        // Clear cache periodically
        this.clearCache();
      },
      this.settings.cacheTTLMinutes * 60 * 1000
    );
  }

  /**
   * Get search suggestions
   */
  async getSuggestions(prefix: string, limit = 10): Promise<string[]> {
    await this.initialize();

    const suggestions = new Set<string>();
    const prefixLower = prefix.toLowerCase();

    // Get suggestions from document titles
    this.documents.forEach((doc) => {
      if (doc.title.toLowerCase().startsWith(prefixLower)) {
        suggestions.add(doc.title);
      }
    });

    // Get suggestions from tags
    this.documents.forEach((doc) => {
      doc.tags.forEach((tag) => {
        if (tag.toLowerCase().startsWith(prefixLower)) {
          suggestions.add(tag);
        }
      });
    });

    // Get suggestions from search history
    const history = await this.storage.getHistory(20);
    history.forEach((item) => {
      if (item.query.query.toLowerCase().startsWith(prefixLower)) {
        suggestions.add(item.query.query);
      }
    });

    return Array.from(suggestions).slice(0, limit);
  }

  /**
   * Export search index
   */
  async exportIndex(): Promise<SearchIndexData> {
    await this.initialize();

    if (!this.index) {
      throw new Error('No index to export');
    }

    return {
      id: 'export',
      indexData: JSON.stringify(this.index.toJSON()),
      version: this.indexVersion,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      documentCount: this.documents.size,
      sizeBytes: 0,
      isCompressed: false,
    };
  }

  /**
   * Import search index
   */
  async importIndex(indexData: SearchIndexData): Promise<void> {
    try {
      this.index = lunr.Index.load(JSON.parse(indexData.indexData));
      this.indexVersion = indexData.version;
      await this.saveIndex();
    } catch (error) {
      console.error('Failed to import index:', error);
      throw new Error('Invalid index data');
    }
  }

  /**
   * Get index statistics
   */
  async getStatistics() {
    return this.storage.getStatistics();
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
    if (this.cacheTimer) {
      clearInterval(this.cacheTimer);
    }
    if (this.worker) {
      this.worker.terminate();
    }
    this.storage.close();
  }
}
